import { GetStaticProps } from 'next';
import { useEffect, useState } from 'react';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';

import { ALL_MENU_QUERY, MenuLocationEnum } from '@/data/allMenuQuery';
import { flatListToHierarchical } from '@/utils/flatListToHierarchical';
import { getApolloClient } from '@/utils/apollo-client';
import { transformMenus } from '@/utils/transformMenus';
import { withLayout } from '@/layout/Layout';
import { Slider } from '@/components/common/Slider';

import { LayoutProps } from '@/layout/Layout/Layout.props';
import { PageProps } from '@/types/types';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { Hero } from '@/components/common/Hero';
import { Heading } from '@/components/typography';
import { Section } from '@/components/common/Section';
import { footerDataTransformer } from '@/utils/footerDataTransformer';
import {
  FooterMenuLocationEnum,
  FOOTER_DATA_QUERY,
  FOOTER_DATA_QUERY_EN,
} from '@/data/footerDataQuery';
import { motion } from 'framer-motion';
import { AnimatedCard } from '@/components/animation/AnimatedCard';
import { PageHead } from '@/components/common/PageHead';
import {
  collageItems,
  heroSliderItems,
  teamAchievements,
  teamCulture,
  teamAdvantages,
  recruitingStages,
} from '@/data/team';
import { Collage } from '@/components/Team/Collage';
import DecorIcon from '@/public/icons/team/footer-bg.svg';
import DecorIconTab from '@/public/icons/team/footer-bg-tab.svg';
import EllipseIcon from '@/public/icons/team/ellipse.svg';
import { RecruitingStagesSlider } from '@/components/Team/RecruitingStagesSlider';
import { Vacancies } from '@/components/Team/Vacancies';
import { Link } from 'react-scroll';
import { TEAM_PAGE_QUERY } from '@/data/teamPageQuery';

const Page = ({ data, language }: PageProps & LayoutProps) => {
  const [isMounted, setIsMounted] = useState(false);
  const {
    seo,
    hero: { hero },
  } = data.pageBy.translation;
  const vacanciesList = data?.allTeam?.nodes ?? {};
  const { t } = useTranslation('common');
  const cultureTitle = t('teamCultureTitle');
  const culturePreTitle = t('teamCulturePreTitle');
  const vacanciesBtn = t('teamVacanciesBtn');
  const advantagesTitle = t('teamAdvantagesTitle');
  const teamFooterTitle = t('teamFooterTitle');
  const teamFooterBtn = t('teamFooterBtn');

  const teamAchievementsData =
    teamAchievements[(language ?? 'UK') as keyof typeof teamAchievements];
  const teamCultureData =
    teamCulture[(language ?? 'UK') as keyof typeof teamCulture];
  const teamAdvantagesData =
    teamAdvantages[(language ?? 'UK') as keyof typeof teamAdvantages];
  const recruitingStagesData =
    recruitingStages[(language ?? 'UK') as keyof typeof recruitingStages];

  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <>
      <PageHead
        title={seo?.title || hero?.title}
        seo={seo || {}}
        canonicalUrl={seo?.canonicalUrl}
        breadcrumbs={seo?.breadcrumbs}
      />
      {isMounted && (
        <>
          <Hero isAccent breadcrumbs={seo?.breadcrumbs} className="team__hero">
            <div className="lg:flex lg:gap-[68px] lg:pb-[50px]">
              <motion.div
                className="mb-10 grid text-white last:mb-0 dark:text-darkFullDark md:mb-[72px] lg:mb-0 lg:mb-[0] lg:block lg:max-w-[465px]"
                initial={{ x: -500, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.25 }}
              >
                <Heading
                  tag="h1"
                  content={hero?.title}
                  color="white"
                  className="mb-8 md:mb-10 lg:mb-5"
                />
                {hero?.descrPtion && (
                  <div
                    className="mb-6 whitespace-pre-line text-lg md:mb-10 md:max-w-[90%] md:text-xl lg:mb-11 lg:max-w-[100%] lg:text-xxl"
                    dangerouslySetInnerHTML={{
                      __html: hero.descrPtion,
                    }}
                  />
                )}
                <Link
                  to="vacancies"
                  href="/team"
                  className="team__hero-btn dark:border-accentAdditional dark:bg-accentAdditional dark:text-darkFullDark 
    dark:hover:border-accentMain dark:hover:bg-accentMain dark:focus:border-accentMain dark:focus:bg-accentMain
        dark:active:border-accentAdditional dark:active:bg-accentMain dark:active:text-darkFullDark"
                  smooth={true}
                  duration={100}
                  offset={-150}
                >
                  {vacanciesBtn}
                </Link>
              </motion.div>
              <motion.div
                initial={{ x: 500, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.25 }}
                className="team__hero-slider-wrapper md:flex md:items-center md:justify-center"
              >
                <span className="hidden w-[256px]  min-w-[256px] md:flex lg:w-[242px]  lg:min-w-[242px]">
                  <EllipseIcon />
                </span>
                <Slider
                  width={512}
                  withArrows={false}
                  tabletShowSlider={1}
                  desktopShowSlider={1}
                  mobShowSlider={1}
                  settings={{
                    autoplay: true,
                    slidesToShow: 1,
                    swipeToSlide: true,
                  }}
                  className="team__hero-slider overflow-hidden"
                >
                  {heroSliderItems.map(item => (
                    <div key={item.id} className="block">
                      {item.image && (
                        <Image
                          src={item.image}
                          alt={item.altText}
                          width={512}
                          height={512}
                          className="h-auto w-full object-cover"
                        />
                      )}
                    </div>
                  ))}
                </Slider>
              </motion.div>
            </div>
          </Hero>
          <Section className="pb-[60px] pt-[60px] md:pb-[100px] md:pt-[100px] lg:pb-[100px] lg:pt-[100px]">
            <ul className="teamAchievements grid justify-center gap-14 md:grid-flow-col  md:grid-rows-[_repeat(2,_min-content)] md:gap-[88px] md:text-lg  lg:grid-rows-[_repeat(1,_min-content)]">
              {teamAchievementsData?.map(item => (
                <li
                  key={item.id}
                  className={`teamAchievements__item md:h-fit md:w-[168px] lg:w-[186px]`}
                >
                  <AnimatedCard className="relative flex flex-col items-center justify-center">
                    <div className="teamAchievements__label mb-6 flex items-center justify-center bg-accentAdditional text-white transition-all duration-300 dark:text-darkFullDark">
                      <p className="teamAchievements__label-text">
                        {item.label}
                      </p>
                    </div>{' '}
                    <p className="teamAchievements__description">
                      {item.description}
                    </p>
                  </AnimatedCard>
                </li>
              ))}
            </ul>
          </Section>
          <Section className="culture pb-[60px] pt-[60px] md:pb-[100px] md:pt-[100px] lg:pb-[100px] lg:pt-[100px]">
            <div className="mb-14 justify-between md:mb-15 lg:flex">
              <Heading
                tag="h2"
                className="mb-4 md:max-w-[519px] lg:mb-0 lg:w-[481px] lg:min-w-[481px]"
                content={cultureTitle}
              />
              <p className="culture__pre-title lg:w-[592px] lg:min-w-[592px]">
                {culturePreTitle}
              </p>
            </div>
            <ul className="culture__list grid gap-12 md:gap-7">
              {teamCultureData?.map(item => (
                <li key={item.id} className={`culture__list-item `}>
                  <AnimatedCard
                    initial={{
                      opacity: 0,
                      borderRadius: '50%',
                    }}
                    duration={1}
                    animate={{
                      opacity: 1,
                      borderRadius: '0',
                    }}
                    className="mb-[26px] h-[64px]  w-[64px] overflow-hidden md:mb-[32px] md:h-[80px] md:w-[80px]"
                  >
                    <Image
                      src={item.icon}
                      alt={'culture icon'}
                      width={80}
                      height={80}
                      className="h-full w-full"
                    />
                  </AnimatedCard>

                  <AnimatedCard
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    damping={12}
                    stiffness={120}
                    type="spring"
                  >
                    <p className="culture__list-item-title">{item.label}</p>
                    <p className="culture__list-item-description">
                      {item.description}
                    </p>
                  </AnimatedCard>
                </li>
              ))}
            </ul>
          </Section>
          <Section className="collage-section pb-[0] pt-[0] md:px-0 md:py-0">
            <AnimatedCard
              // initial={{ scaleX: 0 }}
              // animate={{ scaleX: 1 }}
              // damping={12}
              // stiffness={120}
              type="spring"
            >
              <Collage collageItems={collageItems} />
            </AnimatedCard>
          </Section>
          <Section className="teamAdvantages pb-[60px] pt-[60px] md:pb-[100px] md:pt-[100px] lg:pb-[100px] lg:pt-[100px]">
            <Heading
              tag="h2"
              className="mb-8 md:mb-9 md:max-w-[519px]"
              content={advantagesTitle}
            />
            <ul className="teamAdvantages__list grid gap-[42px]  md:gap-x-10 md:text-lg">
              {teamAdvantagesData.map(item => (
                <li key={item.id} className={`teamAdvantages__list-item `}>
                  <AnimatedCard
                    initial={{
                      opacity: 0,
                      borderRadius: '50%',
                    }}
                    duration={1}
                    animate={{
                      opacity: 1,
                      borderRadius: '0',
                    }}
                    className="mb-6 h-[64px] w-[64px] overflow-hidden md:mb-[32px] md:h-[80px] md:w-[80px]"
                  >
                    <Image
                      src={item.icon}
                      alt={item.description}
                      width={80}
                      height={80}
                      className="h-full w-full"
                    />
                  </AnimatedCard>

                  <AnimatedCard
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    damping={12}
                    stiffness={120}
                    type="spring"
                  >
                    <p className="teamAdvantages__list-item-description">
                      {item.description}
                    </p>
                  </AnimatedCard>
                </li>
              ))}
            </ul>
          </Section>{' '}
          <Section className="recruitingStages pb-15 pr-0 md:pb-[100px] md:pt-[0]">
            <AnimatedCard
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              damping={12}
              stiffness={120}
              type="spring"
            >
              <RecruitingStagesSlider stages={recruitingStagesData} />
            </AnimatedCard>
          </Section>{' '}
          <Section className="vacancies-section pb-15 pt-15 md:pb-[88px] md:pb-[88px] lg:pt-[88px] lg:pt-[88px]">
            <AnimatedCard
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              damping={12}
              stiffness={120}
              type="spring"
            >
              <Vacancies
                language={language || 'UA'}
                vacanciesList={vacanciesList}
              />
            </AnimatedCard>
          </Section>
          <Section className="team-footer bg-accentAdditional pb-0 pt-15 md:pb-[85px] md:pt-[85px] lg:pb-0 lg:pt-0">
            <div className="grid justify-center gap-12 text-white md:flex md:justify-start md:gap-0">
              <div className="align-center z-10 md:w-[336px] lg:flex lg:w-[590px] lg:min-w-[590px] lg:flex-col lg:justify-center">
                <Heading
                  className="team-footer__title"
                  tag="h2"
                  content={teamFooterTitle}
                  color="white"
                />{' '}
                <Link
                  to="vacancies"
                  href="/team"
                  className="team__hero-btn mt-[48px] flex dark:border-accentAdditional dark:bg-accentAdditional 
    dark:text-darkFullDark dark:hover:border-accentMain dark:hover:bg-accentMain dark:focus:border-accentMain
        dark:focus:bg-accentMain dark:active:border-accentAdditional dark:active:bg-accentMain dark:active:text-darkFullDark"
                  smooth={true}
                  duration={100}
                  offset={-50}
                >
                  {teamFooterBtn}
                </Link>
              </div>
              <div className="team-footer__icon grid gap-8 md:gap-0">
                <DecorIcon className="hide hide-image md:hidden lg:block" />
                <DecorIconTab className="hide hide-image hidden md:block md:h-[446px] lg:hidden" />
              </div>
            </div>
          </Section>
        </>
      )}
    </>
  );
};

export default withLayout(Page);

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const client = getApolloClient();
  const location =
    locale == 'uk'
      ? MenuLocationEnum.PRIMARY_NAVIGATION
      : MenuLocationEnum.PRIMARY_NAVIGATION___EN;
  const language = locale?.toUpperCase();
  const teamLanguage = language;

  const { data } = await client.query({
    query: TEAM_PAGE_QUERY,
    variables: {
      language,
      teamLanguage,
    },
  });
  const {
    data: { menuItems },
  } = await client.query({
    query: ALL_MENU_QUERY,
    variables: {
      location,
    },
  });
  const footerLocation =
    locale == 'uk'
      ? FooterMenuLocationEnum.FOOTER_MENU
      : FooterMenuLocationEnum.FOOTER_MENU___EN;

  const { data: footerMenuItems } =
    locale == 'uk'
      ? await client.query({
          query: FOOTER_DATA_QUERY,
          variables: {
            location: footerLocation,
          },
        })
      : await client.query({
          query: FOOTER_DATA_QUERY_EN,
          variables: {
            location: footerLocation,
          },
        });
  const footerData = footerDataTransformer(footerMenuItems);

  const flatList = flatListToHierarchical(menuItems.nodes);
  const menus = transformMenus(flatList);

  return {
    props: {
      data,
      language,
      menus,
      footerData,
      ...(await serverSideTranslations(locale as string, ['common'])),
    },
  };
};
